import React, { useState, useEffect } from 'react';
import { 
  GitMerge, 
  Plus, 
  Trash2, 
  Edit3, 
  Save, 
  Clock, 
  Upload, 
  Eye, 
  X, 
  Image as ImageIcon 
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AdmissionsProcessForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({ 
    title: '', 
    subtitle: '', 
    description: '', 
    steps: [] 
  });
  const [pendingFields, setPendingFields] = useState({ 
    title: false, 
    subtitle: false, 
    description: false 
  });
  const [pendingSteps, setPendingSteps] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [mediaMap, setMediaMap] = useState({});
  const [isAddingStep, setIsAddingStep] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [modalStepData, setModalStepData] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(`${apiBase}/admissions/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (!response.ok) return;
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        const map = {};
        result.data.forEach(m => {
          const id = m.id || m.media_id;
          const rawUrl = m.url || m.file_path || m.filepath || '';
          const fullUrl = rawUrl.startsWith('http') ? rawUrl : (origin ? `${origin}${rawUrl}` : rawUrl);
          if (id && fullUrl) {
            map[id] = fullUrl;
            map[String(id)] = fullUrl;
            const numId = Number(id);
            if (!isNaN(numId)) map[numId] = fullUrl;
          }
          if (m.filename && fullUrl) {
            map[m.filename] = fullUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.warn('[AdmissionsProcessForm] Media load warning:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    const str = String(mediaId).trim();
    if (!str) return null;

    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:')) {
      return str;
    }

    const origin = (apiBase || '').replace(/\/api\/?$/, '');

    if (str.startsWith('/')) {
      return origin ? `${origin}${str}` : str;
    }

    if (mediaMap[str]) return mediaMap[str];
    if (mediaMap[mediaId]) return mediaMap[mediaId];

    if (str.startsWith('admissions_')) {
      return origin ? `${origin}/api/uploads/admissions/${str}` : `/api/uploads/admissions/${str}`;
    }

    const resolved = resolveMediaSrc(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('/')) {
        return origin ? `${origin}${resolved}` : resolved;
      }
      return resolved;
    }

    return str;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await loadMediaMap();
      
      let section = null;

      // 1. Fetch admissions sections via admin endpoint
      try {
        const adminRes = await fetch(`${apiBase}/admissions/cms/sections`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (adminRes.ok) {
          const result = await adminRes.json();
          if (result.ok && Array.isArray(result.data)) {
            section = result.data.find(s => s.section_type === 'admissions-process');
          }
        }
      } catch (adminErr) {
        console.warn('[AdmissionsProcessForm] Admin fetch warning:', adminErr.message);
      }

      // 2. Fallback to published query if needed
      if (!section) {
        try {
          const pubRes = await fetch(`${apiBase}/admissions/cms/sections?published=true`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pubRes.ok) {
            const pubResult = await pubRes.json();
            if (pubResult.ok && Array.isArray(pubResult.data)) {
              section = pubResult.data.find(s => s.section_type === 'admissions-process');
            }
          }
        } catch (pubErr) {
          console.warn('[AdmissionsProcessForm] Public fetch warning:', pubErr.message);
        }
      }

      if (!section) {
        throw new Error('Admissions Application Process section not found');
      }

      setSectionId(section.id);

      let content = section.content_published || section.content || {};
      if (typeof content === 'string') {
        try { content = JSON.parse(content); } catch (e) { content = {}; }
      }
      const en = content?.en || content || {};
      const rawSteps = Array.isArray(en.steps) ? en.steps : [];
      const cleanSteps = rawSteps
        .filter(s => s && typeof s === 'object')
        .map(s => ({
          title: s.title || '',
          description: s.description || '',
          image: s.image || s.photo || s.url || ''
        }));

      const initialData = {
        title: en.title || '',
        subtitle: en.subtitle || en.kicker || '',
        description: en.description || '',
        steps: cleanSteps
      };

      const pMap = { title: false, subtitle: false, description: false };
      const sMap = {};

      // 3. Load pending changes
      try {
        const pRes = await fetch(`${apiBase}/cms/section/${section.id}/with-pending`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (pRes.ok) {
          const pResult = await pRes.json();
          if (pResult.success && Array.isArray(pResult.data?.pending_changes)) {
            pResult.data.pending_changes.forEach(c => {
              if (c.status === 'pending' && (c.locale === 'en' || !c.locale)) {
                let val = c.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                if (['title', 'subtitle', 'description'].includes(c.field_id)) {
                  pMap[c.field_id] = true;
                  if (typeof val === 'string') {
                    initialData[c.field_id] = val;
                  }
                } else if (c.field_id && c.field_id.includes('steps.')) {
                  const m = c.field_id.match(/steps\.(\d+)\.(add|update|delete)/);
                  if (m) {
                    const stepIdx = parseInt(m[1], 10);
                    const op = m[2];
                    sMap[`step_${stepIdx}`] = true;
                    if (op === 'delete') {
                      sMap[`step_${stepIdx}_delete`] = true;
                    } else if (op === 'add') {
                      sMap[`step_${stepIdx}_add`] = true;
                      if (val && !initialData.steps[stepIdx]) {
                        initialData.steps.push(val);
                      }
                    } else if (op === 'update') {
                      if (val && initialData.steps[stepIdx]) {
                        initialData.steps[stepIdx] = { ...initialData.steps[stepIdx], ...val };
                      }
                    }
                  }
                }
              }
            });
          }
        }
      } catch (pErr) {
        console.warn('[AdmissionsProcessForm] Pending changes check warning:', pErr.message);
      }

      setFormData(initialData);
      setOriginalData(JSON.parse(JSON.stringify(initialData)));
      setPendingFields(pMap);
      setPendingSteps(sMap);
    } catch (err) {
      console.error('[AdmissionsProcessForm] Load error:', err.message);
      toast.error('Failed to load admissions process data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitField = async (name, label) => {
    if (!sectionId) { 
      toast.error('Section ID not available'); 
      return; 
    }
    if (formData[name] === originalData[name]) { 
      toast.warning('No changes to submit'); 
      return; 
    }

    setSubmitting(p => ({ ...p, [name]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: name,
          field_name: label || `Admissions Process ${name.replace(/_/g, ' ')}`,
          old_value: originalData[name] || '',
          new_value: formData[name] || '',
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Submitted ${label || name} for Principal approval`);
        setPendingFields(p => ({ ...p, [name]: true }));
        setOriginalData(p => ({ ...p, [name]: formData[name] }));
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for ${label || name} is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [name]: false }));
    }
  };

  const handleOpenAddStep = () => {
    if (formData.steps.length >= 12) {
      toast.warning('Maximum 12 application steps allowed');
      return;
    }
    const newStep = { 
      title: '', 
      description: '', 
      image: '' 
    };
    setIsAddingStep(true);
    setEditingStepIndex(formData.steps.length);
    setModalStepData(newStep);
  };

  const handleOpenEditStep = (idx) => {
    setIsAddingStep(false);
    setEditingStepIndex(idx);
    setModalStepData({ ...formData.steps[idx] });
  };

  const handleCloseModal = () => {
    setIsAddingStep(false);
    setEditingStepIndex(null);
    setModalStepData(null);
    setImageUploading(false);
    setImageProgress(0);
  };

  const handleModalStepChange = (field, value) => {
    if (!modalStepData) return;
    setModalStepData(prev => ({ ...prev, [field]: value }));
  };

  // Upload or Replace Step Photo (Only Replace, No Remove Image)
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are allowed');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const fData = new FormData();
      fData.append('file', file);
      fData.append('section_type', 'admissions-process');

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const res = JSON.parse(xhr.responseText);
            if (res.success || res.ok) {
              const mid = res.media_id || res.id;
              const mediaPath = res.file_path || res.url || res.filepath || (res.filename ? `/api/uploads/admissions/${res.filename}` : '');
              const origin = (apiBase || '').replace(/\/api\/?$/, '');
              const fullUrl = mediaPath 
                ? (mediaPath.startsWith('http') ? mediaPath : `${origin}${mediaPath}`)
                : String(mid || '');

              if (fullUrl) {
                handleModalStepChange('image', fullUrl);
                if (mid) {
                  setMediaMap(prev => ({
                    ...prev,
                    [mid]: fullUrl,
                    [String(mid)]: fullUrl
                  }));
                }
                toast.success('Step photo uploaded successfully');
              } else {
                toast.error('No media reference returned from server');
              }
            } else {
              toast.error(res.error || 'Upload failed');
            }
          } catch (e) {
            toast.error('Invalid server response during image upload');
          }
        } else {
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload error - network failure');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/admissions/cms/media/upload?section_type=admissions-process`);
      const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.withCredentials = true;
      xhr.send(fData);
    } catch (err) {
      toast.error('Error: ' + err.message);
      setImageUploading(false);
    }
  };

  // Submit Step from Modal (Add or Update)
  const handleSaveStepModal = async () => {
    if (!sectionId) { 
      toast.error('Section ID not available'); 
      return; 
    }
    if (!modalStepData || !modalStepData.title?.trim()) { 
      toast.warning('Step title is required'); 
      return; 
    }

    const idx = editingStepIndex;
    const isNew = isAddingStep;
    const op = isNew ? 'add' : 'update';
    const submitKey = isNew ? 'add_step' : `step_${idx}`;

    setSubmitting(p => ({ ...p, [submitKey]: true }));

    const fieldId = `steps.${idx}.${op}`;
    const fieldName = `${isNew ? 'Add' : 'Update'} Admissions Step ${idx + 1}: ${modalStepData.title.trim()}`;
    const oldValue = isNew ? null : (originalData.steps[idx] || {});

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldId,
          field_name: fieldName,
          old_value: oldValue,
          new_value: modalStepData,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || res.ok) {
        toast.success(`Step "${modalStepData.title}" submitted for Principal approval`);
        setPendingSteps(p => ({
          ...p,
          [`step_${idx}`]: true,
          [`step_${idx}_add`]: isNew
        }));

        if (isNew) {
          setFormData(prev => ({
            ...prev,
            steps: [...prev.steps, { ...modalStepData }]
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            steps: prev.steps.map((s, i) => (i === idx ? { ...modalStepData } : s))
          }));
        }

        handleCloseModal();
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for this step is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error submitting step: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [submitKey]: false }));
    }
  };

  // Propose Deletion of Admissions Process Step (Submitted to Principal!)
  const handleDeleteStep = async (idx) => {
    const targetStep = formData.steps[idx];
    if (!targetStep) return;

    if (!window.confirm(`Are you sure you want to propose deleting "${targetStep.title || `Step ${idx + 1}`}"? This will require Principal approval.`)) {
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not available');
      return;
    }

    const key = `del_step_${idx}`;
    setSubmitting(p => ({ ...p, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `steps.${idx}.delete`,
          field_name: `Delete Admissions Step: ${targetStep.title || `Step ${idx + 1}`}`,
          old_value: targetStep,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || res.ok) {
        toast.success(`Deletion of "${targetStep.title || `Step ${idx + 1}`}" submitted for Principal approval`);
        setPendingSteps(p => ({
          ...p,
          [`step_${idx}`]: true,
          [`step_${idx}_delete`]: true
        }));
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for this step is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Delete proposal failed');
        }
      }
    } catch (err) {
      toast.error('Error submitting delete proposal: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading admissions application process configuration...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <GitMerge size={22} />
        </div>
        <div>
          <h2 className="unified-title">Admissions Application Process</h2>
          <p className="unified-subtitle">
            Manage sequential milestones, guidelines, and illustrated step cards shown on the website
          </p>
        </div>
      </div>

      {/* Section Subtitle Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Section Tag / Subtitle</label>
            <p className="unified-field-desc">Kicker label displayed above the process title (e.g. Admissions Process)</p>
          </div>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={submitting.subtitle || pendingFields.subtitle}
            placeholder="e.g. Admissions Process"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle', 'Section Tag / Subtitle')}
            disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} />
            {submitting.subtitle ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Section Title Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Main Section Title</label>
            <p className="unified-field-desc">Headline introducing the step-by-step application walkthrough</p>
          </div>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. Your child's journey starts here, with guidance at every step"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', 'Main Section Title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} />
            {submitting.title ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Section Description Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Section Description</label>
            <p className="unified-field-desc">Explanatory copy guiding families through the interactive carousel milestones</p>
          </div>
          {pendingFields.description && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={submitting.description || pendingFields.description}
            placeholder="e.g. Discover each milestone from inquiry to opportunity. Swipe or use arrows to view..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('description', 'Section Description')}
            disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
          >
            <Save size={14} />
            {submitting.description ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Sequential Process Steps Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Sequential Process Steps ({formData.steps.length})</label>
            <p className="unified-field-desc">Step cards with illustrations explaining each phase of the application</p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleOpenAddStep}
          >
            <Plus size={14} /> Add Step
          </button>
        </div>

        {formData.steps.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 20px', color: '#64748b' }}>
            <GitMerge size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No application steps configured</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Click "Add Step" above to create an application milestone</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
            {formData.steps.map((step, idx) => {
              const key = `step_${idx}`;
              const isPending = pendingSteps[key];
              const isPendingDelete = pendingSteps[`${key}_delete`];
              const resolvedUrl = getMediaUrl(step.image);

              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '14px 18px',
                    background: isPendingDelete ? '#fef2f2' : '#f8fafc',
                    borderRadius: '12px',
                    border: `1px solid ${isPendingDelete ? '#fecaca' : '#e2e8f0'}`,
                    transition: 'all 0.2s ease',
                    opacity: isPendingDelete ? 0.8 : 1
                  }}
                >
                  {/* Thumbnail */}
                  <div
                    style={{
                      width: '120px',
                      height: '80px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      background: '#e2e8f0',
                      flexShrink: 0,
                      position: 'relative'
                    }}
                  >
                    {resolvedUrl ? (
                      <img
                        src={resolvedUrl}
                        alt={step.title || `Step ${idx + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#94a3b8'
                        }}
                      >
                        <ImageIcon size={22} />
                      </div>
                    )}
                    {resolvedUrl && (
                      <button
                        type="button"
                        onClick={() => setLightboxImage(resolvedUrl)}
                        style={{
                          position: 'absolute',
                          bottom: '4px',
                          right: '4px',
                          background: 'rgba(0,0,0,0.65)',
                          color: '#fff',
                          border: 'none',
                          padding: '3px 5px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="View Full Photo"
                      >
                        <Eye size={11} />
                      </button>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '6px',
                          background: isPendingDelete ? '#fee2e2' : '#eff6ff',
                          color: isPendingDelete ? '#991b1b' : '#1d4ed8',
                          fontSize: '11px',
                          fontWeight: 700
                        }}
                      >
                        Step {idx + 1}
                      </span>
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 800, color: isPendingDelete ? '#991b1b' : '#0f172a' }}>
                        {step.title || 'Untitled Step'}
                      </h4>
                      {isPendingDelete ? (
                        <span className="unified-pending-pill" style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' }}>
                          <Trash2 size={10} /> Pending Deletion
                        </span>
                      ) : isPending ? (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Review
                        </span>
                      ) : null}
                    </div>

                    <p
                      style={{
                        margin: 0,
                        fontSize: '13px',
                        color: isPendingDelete ? '#7f1d1d' : '#475569',
                        lineHeight: 1.5,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {step.description || '(No description entered yet)'}
                    </p>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={() => handleOpenEditStep(idx)}
                      disabled={isPending || isPendingDelete}
                    >
                      <Edit3 size={14} /> Edit Step
                    </button>
                    {!isPendingDelete && (
                      <button
                        type="button"
                        onClick={() => handleDeleteStep(idx)}
                        disabled={submitting[`del_step_${idx}`] || isPending}
                        style={{
                          padding: '8px 12px',
                          color: '#ef4444',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '8px',
                          cursor: (submitting[`del_step_${idx}`] || isPending) ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontWeight: 600,
                          fontSize: '12px'
                        }}
                        title="Propose deletion of this step"
                      >
                        <Trash2 size={14} />
                        <span>{submitting[`del_step_${idx}`] ? 'Submitting...' : 'Delete'}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add / Edit Step Modal */}
      {(isAddingStep || editingStepIndex !== null) && modalStepData && (
        <div
          className="unified-modal-overlay"
          onClick={handleCloseModal}
        >
          <div className="unified-modal" style={{ maxWidth: '640px' }} onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>
                  {isAddingStep ? 'Add Admissions Step' : `Edit Step: ${modalStepData.title || `Step ${(editingStepIndex || 0) + 1}`}`}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Configure milestone title, instructional guidance, and illustrative graphic
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Photo Preview & Upload (Only Replace, No Remove) */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0'
                }}
              >
                <div
                  style={{
                    width: '140px',
                    height: '95px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#e2e8f0',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #cbd5e1',
                    position: 'relative'
                  }}
                >
                  {modalStepData.image && getMediaUrl(modalStepData.image) ? (
                    <img
                      src={getMediaUrl(modalStepData.image)}
                      alt="Step preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                      <ImageIcon size={28} style={{ margin: '0 auto 4px', opacity: 0.5 }} />
                      <span style={{ fontSize: '11px', display: 'block' }}>No image</span>
                    </div>
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <input
                    type="file"
                    id="step-modal-file-upload"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    disabled={imageUploading}
                    style={{ display: 'none' }}
                  />
                  <label
                    htmlFor="step-modal-file-upload"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '9px 16px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      cursor: imageUploading ? 'not-allowed' : 'pointer',
                      color: '#2563eb',
                      fontWeight: 600,
                      fontSize: '13px'
                    }}
                  >
                    <Upload size={14} />
                    {imageUploading
                      ? `Uploading (${imageProgress}%)...`
                      : (modalStepData.image ? 'Replace Step Image' : 'Upload Step Image')}
                  </label>
                  <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#64748b' }}>
                    Recommended: 600×350px • JPG, PNG, WebP (Max 5MB) • Only Replace
                  </p>
                </div>
              </div>

              {/* Title */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Step Title *
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalStepData.title || ''}
                  onChange={(e) => handleModalStepChange('title', e.target.value)}
                  placeholder="e.g. Visit Admission School"
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Step Description / Instructions
                </label>
                <textarea
                  className="unified-textarea"
                  rows={4}
                  value={modalStepData.description || ''}
                  onChange={(e) => handleModalStepChange('description', e.target.value)}
                  placeholder="Explain what parents and prospective students do during this step..."
                />
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                disabled={submitting[isAddingStep ? 'add_step' : `step_${editingStepIndex}`] || !modalStepData.title?.trim()}
                onClick={handleSaveStepModal}
              >
                <Save size={14} />
                {submitting[isAddingStep ? 'add_step' : `step_${editingStepIndex}`]
                  ? 'Submitting...'
                  : (isAddingStep ? 'Save & Submit New Step' : 'Save & Submit Step')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              background: '#000',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10
              }}
              aria-label="Close photo preview"
            >
              <X size={18} />
            </button>
            <img
              src={lightboxImage}
              alt="Full Preview"
              style={{ maxWidth: '100%', maxHeight: '85vh', display: 'block', objectFit: 'contain' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
